import { useEffect, type ReactNode } from 'react'
import { createBrowserRouter, createHashRouter, Navigate, Outlet, useLocation } from 'react-router-dom'
import { isVkDocument } from '../platform/detect'
import { useMeQuery } from '../api/endpoints/auth'
import { EventStream } from '../api/events'
import { Shell } from '../shell/Shell'
import { AuthFrame } from '../screens/auth/AuthFrame'
import { Landing } from '../screens/auth/Landing'
import { Login } from '../screens/auth/Login'
import { Register } from '../screens/auth/Register'
import { HeroNew } from '../screens/auth/HeroNew'
import { Location } from '../screens/location/Location'
import { MapScreen } from '../screens/map/MapScreen'
import { HeroScreen } from '../screens/hero/HeroScreen'
import { ProfessionsScreen } from '../screens/professions/ProfessionsScreen'
import { MentorScreen } from '../screens/mentors/MentorScreen'
import { SkillsScreen } from '../screens/skills/SkillsScreen'
import { Inventory } from '../screens/inventory/Inventory'
import { BattleScreen } from '../screens/battle/BattleScreen'
import { Journal } from '../screens/journal/Journal'
import { Arena } from '../screens/arena/Arena'
import { Clans } from '../screens/clans/Clans'
import { Faith } from '../screens/faith/Faith'
import { TradersScreen } from '../screens/shop/TradersScreen'
import { ShopScreen } from '../screens/shop/ShopScreen'
import { VkPaymentTest } from '../screens/settings/VkPaymentTest'
import { Settings } from '../screens/settings/Settings'
import { Failure, Loading } from '../ui/State'
import { ErrorBoundary } from './ErrorBoundary'
import { useAppDispatch, useAppSelector } from './store'

/** Screens after sign-in: no session → the entry for this platform. */
function RequireSession({ children }: { children: ReactNode }) {
  const signedIn = useAppSelector(s => s.session.signedIn)
  const platform = useAppSelector(s => s.session.platform)
  const { pathname } = useLocation()
  if (!signedIn) return <Navigate to={platform === 'web' ? '/login' : '/'} replace state={{ from: pathname }} />
  return <>{children}</>
}

/** The account must have a hero to play; reads /me once. */
function RequireHero({ children }: { children: ReactNode }) {
  const me = useMeQuery()
  if (me.data && !me.data.has_hero) return <Navigate to="/hero/new" replace />
  if (me.data) return <>{children}</>
  return (
    <AuthFrame title="Межа">
      {me.isError ? <Failure error={me.error} retry={() => void me.refetch()} what="Данные учётной записи" /> : <Loading text="Входим…" />}
    </AuthFrame>
  )
}

/** The player event stream lives while the game shell is mounted. */
function LiveEvents() {
  const dispatch = useAppDispatch()
  useEffect(() => {
    const stream = new EventStream(dispatch)
    stream.start()
    return () => stream.stop()
  }, [dispatch])
  return null
}

function Game() {
  return (
    <RequireSession>
      <RequireHero>
        <LiveEvents />
        <Shell />
      </RequireHero>
    </RequireSession>
  )
}

function Root() {
  return <ErrorBoundary><Outlet /></ErrorBoundary>
}

const dev = import.meta.env.DEV
  ? [{ path: '/dev/gallery', lazy: async () => ({ Component: (await import('../dev/GameGallery')).GameGallery }) }]
  : []

const createRouter = isVkDocument() ? createHashRouter : createBrowserRouter
export const router = createRouter([
  {
    element: <Root />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
      { path: '/hero/new', element: <RequireSession><HeroNew /></RequireSession> },
      {
        path: '/play',
        element: <Game />,
        children: [
          { index: true, element: <Location /> },
          { path: 'map', element: <MapScreen /> },
          { path: 'hero', element: <HeroScreen /> },
          { path: 'skills', element: <SkillsScreen /> },
          { path: 'professions', element: <ProfessionsScreen /> },
      { path: 'mentor/:id', element: <MentorScreen /> },
          { path: 'inventory', element: <Inventory /> },
          { path: 'journal', element: <Journal /> },
          { path: 'battle/:id', element: <BattleScreen /> },
          { path: 'arena', element: <Arena /> },
          { path: 'clans', element: <Clans /> },
          { path: 'faith', element: <Faith /> },
          { path: 'shop/:id', element: <ShopScreen /> },
          { path: 'traders', element: <TradersScreen /> },
          { path: 'vk-payment-test', element: <VkPaymentTest /> },
          { path: 'settings', element: <Settings /> },
          { path: '*', element: <Navigate to="/play" replace /> },
        ],
      },
      ...dev,
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
], { future: { v7_relativeSplatPath: true, v7_fetcherPersist: true, v7_normalizeFormMethod: true, v7_partialHydration: true, v7_skipActionErrorRevalidation: true } })
